/* ==============================================================================
   Cliente do gateway
   ==============================================================================
   Mantém o WebSocket vivo, bate o coração, reconecta com recuo exponencial, e
   entrega os eventos por assinatura.

   Três decisões que definem o comportamento
   =========================================

   1. **Reconexão tenta RESUME primeiro.**
      Depois de uma oscilação de rede, `IDENTIFY` do zero descartaria a sessão de
      voz e o cliente reconstruiria a malha inteira — desligando e religando
      todas as chamadas por causa de dois segundos de rede ruim. `RESUME` mantém
      a sessão e recebe os eventos perdidos.

   2. **Recuo exponencial COM ruído aleatório.**
      Sem ruído, todos os clientes que caíram no mesmo deploy reconectam no mesmo
      instante — e derrubam o servidor que acabou de subir. O ruído espalha.

   3. **`INVALID_SESSION` não é tratado como erro de rede.**
      Ele significa "não insista com RESUME". Sem essa distinção, o cliente
      entraria em laço: RESUME → INVALID_SESSION → RESUME.

   O que o cliente NÃO faz: reconectar quando o servidor fecha com um código não
   recuperável (token inválido, versão errada). Insistir nesses casos é o laço
   infinito clássico, e o `recoverable` de `CloseCode` existe para isso.
   ============================================================================== */

import {
  CloseCode,
  GATEWAY_VERSION,
  Op,
  type GatewayFrame,
  type VoicePeer,
} from '@concord/shared';

export type GatewayStatus =
  | 'idle'
  | 'connecting'
  | 'identifying'
  | 'ready'
  | 'reconnecting'
  | 'closed';

export interface GatewayDevice {
  name: string;
  platform: 'windows' | 'macos' | 'linux' | 'web' | 'android' | 'ios';
  appVersion: string;
}

export interface GatewayClientOptions {
  /** URL absoluta ou relativa. `wss://` é derivado do protocolo da página. */
  url?: string;
  getToken: () => Promise<string | null>;
  device: GatewayDevice;
  capabilities?: { multiScreen?: boolean; e2ee?: boolean };
  /** Injetável para teste. */
  socketFactory?: (url: string) => WebSocket;
  onStatusChange?: (status: GatewayStatus) => void;
  /** Chamado quando o servidor recusa a credencial: exige novo login. */
  onAuthFailure?: () => void;
}

type Handler = (data: unknown) => void;

/** Estado da sessão de voz, mantido pelo cliente a partir dos eventos. */
export interface VoiceSessionState {
  channelId: string;
  /** Nossa sessão de gateway. Decide polite/impolite. */
  sessionId: string;
  peers: Map<string, VoicePeer>;
  iceServers: RTCIceServer[];
  iceExpiresAt: Date;
  maxParticipants: number;
}

export class GatewayClient {
  private socket: WebSocket | null = null;
  private status: GatewayStatus = 'idle';

  /** Sessão do gateway, para RESUME. */
  private sessionId: string | null = null;
  /** Último `s` recebido, marca d'água do RESUME. */
  private seq = 0;

  private heartbeatTimer: ReturnType<typeof setInterval> | undefined;
  private reconnectTimer: ReturnType<typeof setTimeout> | undefined;
  private attempt = 0;
  /** `true` quando `close()` foi chamado: não reconecta. */
  private disposed = false;

  private readonly handlers = new Map<string, Set<Handler>>();
  private readonly options: GatewayClientOptions;

  /** Estado de voz derivado dos eventos. `null` fora de chamada. */
  voice: VoiceSessionState | null = null;

  constructor(options: GatewayClientOptions) {
    this.options = options;
  }

  /* ==========================================================================
     Assinatura de eventos
     ========================================================================== */

  /** Registra um ouvinte. Devolve a função de cancelamento. */
  on(event: string, handler: Handler): () => void {
    const set = this.handlers.get(event) ?? new Set();
    set.add(handler);
    this.handlers.set(event, set);
    return () => {
      set.delete(handler);
      if (set.size === 0) this.handlers.delete(event);
    };
  }

  private emit(event: string, data: unknown): void {
    for (const handler of this.handlers.get(event) ?? []) {
      try {
        handler(data);
      } catch (error) {
        /*
         * Um ouvinte que lança NÃO derruba os outros nem a conexão.
         *
         * Sem o try, um erro de render num componente que ouve MESSAGE_CREATE
         * abortaria o processamento do frame — e a mensagem seguinte também, e a
         * conexão pareceria travada por um bug de UI.
         */
        console.error(`[gateway] ouvinte de ${event} lançou:`, error);
      }
    }
  }

  private setStatus(status: GatewayStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.options.onStatusChange?.(status);
  }

  get currentStatus(): GatewayStatus {
    return this.status;
  }

  /* ==========================================================================
     Conexão
     ========================================================================== */

  connect(): void {
    this.disposed = false;
    this.open();
  }

  private resolveUrl(): string {
    if (this.options.url) return this.options.url;
    /*
     * Derivado da página, e `wss` quando ela é `https`.
     *
     * Fixar `wss://` sempre quebraria o desenvolvimento em `http://localhost`; e
     * fixar `ws://` quebraria produção, porque navegador recusa WebSocket
     * inseguro numa página segura (mixed content).
     */
    const protocol = globalThis.location?.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = globalThis.location?.host ?? 'localhost';
    return `${protocol}//${host}/gateway?v=${GATEWAY_VERSION}`;
  }

  private open(): void {
    if (this.disposed) return;

    this.setStatus(this.sessionId !== null ? 'reconnecting' : 'connecting');

    const factory =
      this.options.socketFactory ?? ((url: string) => new WebSocket(url));
    const socket = factory(this.resolveUrl());
    this.socket = socket;

    socket.addEventListener('message', (event: MessageEvent) => {
      void this.onFrame(event.data);
    });

    socket.addEventListener('close', (event: CloseEvent) => {
      this.stopHeartbeat();

      /* ====================================================================
         A sessão de voz MORRE com o socket
         ====================================================================
         Isto faltava, e era a razão de "o reconectar não funciona ao cair a
         internet e voltar".

         Participar de uma sala de voz é estado de SESSÃO no servidor: a linha
         em `voice_states` é gravada com `gateway_session_id`, e quando o socket
         cai o servidor apaga a linha daquela sessão. Um socket novo é uma
         sessão nova, sem voz nenhuma.

         O cliente não fazia essa conta. `this.voice` sobrevivia à queda, e
         então:

           - `state.connected` continuava `true`, com a interface mostrando a
             chamada em curso — mas a malha estava morta e não havia com quem
             falar;

           - pior, `considerRejoin()` começa com `if (this.gateway.voice !==
             null) return`. Essa saída existe para não reentrar numa chamada em
             que já se está. Com o valor velho preso ali, ela disparava SEMPRE,
             e a reentrada automática — que existe, está testada e funciona —
             nunca chegava a ser tentada.

         Ou seja: o mecanismo de reconexão estava correto e era desarmado por um
         campo que ninguém limpava.

         A INTENÇÃO de estar na chamada não é apagada aqui. Ela vive em
         `desiredChannelId`, no motor de voz, e é justamente o que faz a
         reentrada acontecer no próximo READY.
      ==================================================================== */
      const perdeuVoz = this.voice !== null;
      this.voice = null;
      /*
       * O aviso é EMITIDO, não deduzido.
       *
       * `state.connected` sai de `gateway.voice`, mas nada no motor de voz
       * observa o socket — sem este evento, a interface continuaria pintando a
       * chamada como ativa até algum outro evento provocar um `publish()`, o
       * que numa queda de rede pode não acontecer nunca.
       */
      if (perdeuVoz) this.emit('VOICE_SESSION_LOST', {});

      /*
       * A decisão de reconectar sai do CÓDIGO de fechamento, não de tentativa e
       * erro.
       *
       * `CloseCode` marca cada código como recuperável ou não. Insistir num
       * `AUTHENTICATION_FAILED` produziria o laço infinito clássico — e o
       * usuário veria a interface "reconectando" para sempre sem nunca ser
       * levado ao login.
       */
      const spec = Object.values(CloseCode).find((c) => c.code === event.code);
      const recoverable = spec?.recoverable ?? true;

      if (!recoverable) {
        this.setStatus('closed');
        this.sessionId = null;
        this.options.onAuthFailure?.();
        return;
      }

      if (!this.disposed) this.scheduleReconnect();
      else this.setStatus('closed');
    });

    socket.addEventListener('error', () => {
      // O evento `error` do WebSocket não traz detalhe por design do navegador
      // (evita revelar informação de rede). O `close` que vem em seguida é que
      // carrega o código, então o tratamento fica lá.
    });
  }

  private scheduleReconnect(): void {
    this.attempt += 1;
    this.setStatus('reconnecting');

    /*
     * Recuo exponencial com teto e RUÍDO.
     *
     * 1s, 2s, 4s… até 30s. O ruído de ±30% é o que impede que todos os clientes
     * derrubados por um deploy voltem no mesmo milissegundo e derrubem o
     * servidor que acabou de subir — o "thundering herd" que transforma um
     * deploy de 5 segundos numa indisponibilidade de minutos.
     */
    const base = Math.min(30_000, 1000 * 2 ** (this.attempt - 1));
    const jitter = base * (Math.random() * 0.6 - 0.3);
    const delay = Math.max(500, Math.round(base + jitter));

    this.reconnectTimer = setTimeout(() => this.open(), delay);
  }

  private async onFrame(raw: unknown): Promise<void> {
    if (typeof raw !== 'string') return;

    let frame: GatewayFrame;
    try {
      frame = JSON.parse(raw) as GatewayFrame;
    } catch {
      return;
    }

    switch (frame.op) {
      case Op.HELLO: {
        const hello = frame.d as { heartbeat_interval_ms: number };
        this.startHeartbeat(hello.heartbeat_interval_ms);
        await this.authenticate();
        return;
      }

      case Op.HEARTBEAT_ACK:
        return;

      case Op.RECONNECT:
        // O servidor pediu para reconectar (deploy, rebalanceamento). Fecha com
        // código recuperável para o fluxo normal de RESUME assumir.
        this.socket?.close(CloseCode.UNKNOWN_ERROR.code, 'servidor pediu reconexão');
        return;

      case Op.INVALID_SESSION: {
        /*
         * Não é erro de rede: é "não insista com RESUME".
         *
         * Zerar `sessionId` e `seq` é o que faz a próxima tentativa ser um
         * IDENTIFY completo. Sem isso, o cliente repetiria RESUME e receberia
         * INVALID_SESSION para sempre.
         */
        this.sessionId = null;
        this.seq = 0;
        this.voice = null;
        this.emit('SESSION_INVALIDATED', frame.d);
        await this.authenticate();
        return;
      }

      case Op.DISPATCH: {
        if (typeof frame.s === 'number') this.seq = frame.s;
        const event = frame.t ?? '';
        this.handleDispatch(event, frame.d);
        this.emit(event, frame.d);
        return;
      }

      default:
        return;
    }
  }

  /**
   * Mantém o estado de voz do cliente a partir dos eventos.
   *
   * Fica aqui e não no componente de UI porque o motor de mídia também precisa
   * dele — e dois consumidores derivando o mesmo estado de forma independente é
   * como eles divergem. O componente lê `client.voice`.
   */
  private handleDispatch(event: string, data: unknown): void {
    switch (event) {
      case 'READY': {
        const ready = data as { session_id: string };
        this.sessionId = ready.session_id;
        this.attempt = 0;
        this.setStatus('ready');
        return;
      }

      case 'RESUMED':
        this.attempt = 0;
        this.setStatus('ready');
        return;

      case 'VOICE_READY': {
        const payload = data as {
          channel_id: string;
          session_id: string;
          peers: VoicePeer[];
          ice: { ice_servers: RTCIceServer[]; expires_at: string };
          max_participants: number;
        };
        this.voice = {
          channelId: payload.channel_id,
          sessionId: payload.session_id,
          peers: new Map(payload.peers.map((p) => [p.session_id, p])),
          iceServers: payload.ice.ice_servers,
          iceExpiresAt: new Date(payload.ice.expires_at),
          maxParticipants: payload.max_participants,
        };
        return;
      }

      case 'VOICE_PEER_JOIN': {
        const payload = data as { channel_id: string; peer: VoicePeer };
        if (this.voice?.channelId !== payload.channel_id) return;
        this.voice.peers.set(payload.peer.session_id, payload.peer);
        return;
      }

      case 'VOICE_PEER_LEAVE': {
        const payload = data as { channel_id: string; session_id: string };
        if (this.voice?.channelId !== payload.channel_id) return;
        this.voice.peers.delete(payload.session_id);
        return;
      }

      case 'VOICE_STATE_UPDATE': {
        const payload = data as VoicePeer & { channel_id: string; joined?: boolean };
        if (this.voice?.channelId !== payload.channel_id) return;
        // O evento chega para a guild inteira, inclusive para quem não está na
        // sala; o filtro por channelId acima já cuida disso. Aqui só atualiza
        // quem já está no mapa — um peer desconhecido vem por PEER_JOIN.
        const existing = this.voice.peers.get(payload.session_id);
        if (existing) {
          this.voice.peers.set(payload.session_id, {
            ...existing,
            self_mute: payload.self_mute ?? existing.self_mute,
            self_deaf: payload.self_deaf ?? existing.self_deaf,
            camera_on: payload.camera_on ?? existing.camera_on,
            screen_count: payload.screen_count ?? existing.screen_count,
          });
        }
        return;
      }

      case 'VOICE_MOVED': {
        const payload = data as { to_channel_id: string; peers: VoicePeer[] };
        if (!this.voice) return;
        /*
         * Movido pela moderação: a malha antiga toda é descartada.
         *
         * O motor de mídia observa `VOICE_MOVED` e fecha as RTCPeerConnection
         * anteriores. Manter alguma seria manter áudio fluindo para gente da
         * sala anterior, que não faz parte da conversa nova.
         */
        this.voice = {
          ...this.voice,
          channelId: payload.to_channel_id,
          peers: new Map(payload.peers.map((p) => [p.session_id, p])),
        };
        return;
      }

      default:
        return;
    }
  }

  private async authenticate(): Promise<void> {
    const token = await this.options.getToken();
    if (token === null) {
      this.socket?.close(CloseCode.AUTHENTICATION_FAILED.code, 'sem token');
      this.options.onAuthFailure?.();
      return;
    }

    this.setStatus('identifying');

    // RESUME quando há sessão a retomar; IDENTIFY quando não há. É o que evita
    // reconstruir a malha de voz por causa de uma oscilação de rede.
    if (this.sessionId !== null) {
      this.send(Op.RESUME, {
        token,
        session_id: this.sessionId,
        seq: this.seq,
      });
      return;
    }

    this.send(Op.IDENTIFY, {
      token,
      device: {
        name: this.options.device.name,
        platform: this.options.device.platform,
        app_version: this.options.device.appVersion,
      },
      capabilities: {
        multi_screen: this.options.capabilities?.multiScreen ?? false,
        e2ee: this.options.capabilities?.e2ee ?? false,
      },
    });
  }

  private startHeartbeat(intervalMs: number): void {
    this.stopHeartbeat();
    /*
     * O heartbeat carrega o último `seq` recebido.
     *
     * Ele é informativo para o servidor, mas é o que permite detectar do lado do
     * servidor um cliente que está recebendo e não processando. Mandar `null`
     * sempre funcionaria e perderia essa informação.
     */
    this.heartbeatTimer = setInterval(() => {
      this.send(Op.HEARTBEAT, { seq: this.seq > 0 ? this.seq : null });
    }, intervalMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== undefined) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = undefined;
    }
  }

  /* ==========================================================================
     Envio
     ========================================================================== */

  private send(op: number, data?: unknown): boolean {
    if (this.socket?.readyState !== WebSocket.OPEN) return false;
    this.socket.send(JSON.stringify({ op, ...(data !== undefined ? { d: data } : {}) }));
    return true;
  }

  joinVoice(channelId: string, options: { selfMute?: boolean; selfDeaf?: boolean } = {}): boolean {
    return this.send(Op.VOICE_JOIN, {
      channel_id: channelId,
      self_mute: options.selfMute ?? false,
      self_deaf: options.selfDeaf ?? false,
    });
  }

  /**
   * "Estou digitando neste canal."
   *
   * Dispara e esquece. Não devolve promessa e não espera confirmação: o
   * servidor reencaminha sem guardar estado, e não há nada que possa dar errado
   * que valha a pena contar a quem está escrevendo.
   *
   * O ESTRANGULAMENTO É DE QUEM CHAMA, não daqui. `Composer` já limita a um
   * envio a cada 3 segundos — um evento por tecla inundaria o gateway, e o
   * indicador só precisa aparecer, não ser preciso ao milissegundo.
   */
  sendTyping(channelId: string): boolean {
    return this.send(Op.TYPING, { channel_id: channelId });
  }

  leaveVoice(): boolean {
    const sent = this.send(Op.VOICE_LEAVE);
    // Limpa o estado local imediatamente, sem esperar o eco do servidor: a UI
    // precisa refletir "saí" no clique, e o eco pode nunca chegar se a conexão
    // caiu justamente agora.
    this.voice = null;
    return sent;
  }

  updateVoiceState(patch: {
    selfMute?: boolean;
    selfDeaf?: boolean;
    cameraOn?: boolean;
    screenCount?: number;
  }): boolean {
    return this.send(Op.VOICE_STATE, {
      ...(patch.selfMute !== undefined ? { self_mute: patch.selfMute } : {}),
      ...(patch.selfDeaf !== undefined ? { self_deaf: patch.selfDeaf } : {}),
      ...(patch.cameraOn !== undefined ? { camera_on: patch.cameraOn } : {}),
      ...(patch.screenCount !== undefined ? { screen_count: patch.screenCount } : {}),
    });
  }

  /** Encaminha sinalização para UM par. O servidor não interpreta o conteúdo. */
  signal(
    toUserId: string,
    toSessionId: string,
    payload:
      | { kind: 'offer'; sdp: string }
      | { kind: 'answer'; sdp: string }
      | {
          kind: 'ice';
          candidate: string;
          sdp_mid: string | null;
          sdp_m_line_index: number | null;
          username_fragment?: string | null;
        }
      | { kind: 'ice_complete' }
      | { kind: 'restart' },
  ): boolean {
    return this.send(Op.SIGNAL_RELAY, {
      to_user_id: toUserId,
      to_session_id: toSessionId,
      payload,
    });
  }

  updatePresence(status: 'online' | 'idle' | 'dnd' | 'offline', customStatus?: string | null): boolean {
    return this.send(Op.PRESENCE_UPDATE, {
      status,
      ...(customStatus !== undefined ? { custom_status: customStatus } : {}),
    });
  }

  /** Encerra e não reconecta. */
  close(): void {
    this.disposed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer !== undefined) clearTimeout(this.reconnectTimer);
    this.voice = null;
    this.sessionId = null;
    this.socket?.close(1000, 'cliente encerrando');
    this.socket = null;
    this.setStatus('closed');
  }
}
